import React, { ReactElement, ReactNode, useEffect, useState } from "react";
import { useHistory } from "react-router-dom";
import type { Location } from "history";
import ActionDialog from "./ActionDialog";
import InformationIcon from "../../images/information-icon.png";

interface UnsavedChangesPromptProps {
  isDirty: boolean;
  title?: string;
  description?: ReactNode;
  stayButtonText?: string;
  leaveButtonText?: string;
}

const DEFAULT_TITLE = "UNSAVED CHANGES";
const DEFAULT_DESCRIPTION: ReactNode = (
  <>
    <div>You have unsaved changes.</div>
    <div>
      Click <strong>Update</strong> at the bottom of the page to save them.
    </div>
  </>
);

const UnsavedChangesPrompt = ({
  isDirty,
  title = DEFAULT_TITLE,
  description = DEFAULT_DESCRIPTION,
  stayButtonText = "Stay on Page",
  leaveButtonText = "Leave Without Saving",
}: UnsavedChangesPromptProps): ReactElement | null => {
  const history = useHistory();
  const [pendingLocation, setPendingLocation] = useState<Location | null>(null);
  const [confirmed, setConfirmed] = useState(false);

  // Block in-app route changes while dirty
  useEffect(() => {
    if (!isDirty) return undefined;
    const unblock = history.block((nextLocation) => {
      if (!confirmed) {
        setPendingLocation(nextLocation as Location);
        return false;
      }
      return undefined;
    });
    return unblock;
  }, [isDirty, history, confirmed]);

  // Warn on full-page close / refresh while dirty
  useEffect(() => {
    if (!isDirty) return undefined;
    const handler = (e: BeforeUnloadEvent) => {
      e.preventDefault();
      e.returnValue = "";
      return "";
    };
    window.addEventListener("beforeunload", handler);
    return () => window.removeEventListener("beforeunload", handler);
  }, [isDirty]);

  // Once the user confirms leaving, actually navigate to the pending location
  useEffect(() => {
    if (confirmed && pendingLocation) {
      history.push(pendingLocation.pathname + pendingLocation.search + pendingLocation.hash);
    }
  }, [confirmed, pendingLocation, history]);

  if (!pendingLocation) return null;

  return (
    <ActionDialog
      open={Boolean(pendingLocation)}
      onClose={() => setPendingLocation(null)}
      icon={<img src={InformationIcon} alt="Information" style={{ width: 80, height: 80 }} />}
      iconBgColor="transparent"
      iconSize={80}
      title={title}
      description={description}
      buttonText={stayButtonText}
      onButtonClick={() => setPendingLocation(null)}
      secondaryButtonText={leaveButtonText}
      onSecondaryButtonClick={() => setConfirmed(true)}
      maxWidth="580px"
    />
  );
};

export default UnsavedChangesPrompt;
